import { getDesktopBridge, isDesktopPlatform } from '@/lib/desktop/bridge';

export interface SupabaseStorageAdapter {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

const unavailableDesktopStorageAdapter: SupabaseStorageAdapter = {
  getItem: async () => await Promise.resolve<string | null>(null),
  setItem: async () =>
    await Promise.reject(new Error('Desktop secure auth storage is unavailable')),
  removeItem: async () => await Promise.resolve(),
};

// Durable record of the last explicit persistence choice. The in-memory flag
// resets on every renderer reload, but auth callbacks (OAuth deep link, magic
// link) can complete in a fresh renderer that never revisits the login form —
// without this record such a completion would silently write an opted-out
// session to the Keychain. Absence of the record means "persist" (the desktop
// default); the login form's own sessionStorage mirror only pre-fills the
// checkbox and is never read for enforcement.
const DESKTOP_AUTH_PERSISTENCE_KEY = 'pp-desktop-auth-persistence';

function readStoredDesktopAuthPersistence(): boolean {
  try {
    return globalThis.localStorage.getItem(DESKTOP_AUTH_PERSISTENCE_KEY) !== 'false';
  } catch {
    return true;
  }
}

let persistDesktopAuthSession = readStoredDesktopAuthPersistence();
const volatileDesktopAuthStorage = new Map<string, string>();

/** Controls whether future desktop auth writes go to Keychain or process memory. */
export function configureDesktopAuthPersistence(rememberSession: boolean): void {
  if (persistDesktopAuthSession !== rememberSession) {
    volatileDesktopAuthStorage.clear();
  }
  persistDesktopAuthSession = rememberSession;
  try {
    globalThis.localStorage.setItem(DESKTOP_AUTH_PERSISTENCE_KEY, String(rememberSession));
  } catch {
    // Storage unavailable (private mode / test env): the in-memory flag still
    // governs this renderer session; only reload survival is lost.
  }
}

function createDesktopStorageAdapter(storage: SupabaseStorageAdapter): SupabaseStorageAdapter {
  return {
    getItem: async (key: string) => {
      if (!persistDesktopAuthSession) {
        return volatileDesktopAuthStorage.get(key) ?? null;
      }
      return await storage.getItem(key);
    },
    setItem: async (key: string, value: string) => {
      if (persistDesktopAuthSession) {
        volatileDesktopAuthStorage.delete(key);
        await storage.setItem(key, value);
        return;
      }
      await storage.removeItem(key);
      volatileDesktopAuthStorage.set(key, value);
    },
    removeItem: async (key: string) => {
      volatileDesktopAuthStorage.delete(key);
      await storage.removeItem(key);
    },
  };
}

export function __resetDesktopAuthPersistenceForTests(): void {
  persistDesktopAuthSession = true;
  volatileDesktopAuthStorage.clear();
  try {
    globalThis.localStorage.removeItem(DESKTOP_AUTH_PERSISTENCE_KEY);
  } catch {
    // Storage unavailable: nothing durable to clear.
  }
}

export function getSupabaseAuthStorage(): SupabaseStorageAdapter {
  const desktop = getDesktopBridge();
  if (desktop) {
    return createDesktopStorageAdapter(desktop.authStorage);
  }
  if (isDesktopPlatform()) {
    return unavailableDesktopStorageAdapter;
  }
  return globalThis.localStorage;
}
